import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import PhoneNumberDetails from '../components/PhoneNumberDetails';
import { useAuth } from '../contexts/AuthContext';
import {
  canonicalizeUrlParam,
  getPhoneDisplayLines,
  getAdjacentCanonicalIds,
  countries,
} from '../utils/phoneCanonical';
import { getCategoryById } from '../utils/reportCategories';
import {
  getPhoneNumber,
  upsertPhoneNumberStub,
  getApprovedReports,
  getPhoneNumbersByIds,
} from '../services/firestoreService';
import AdjacentNumbers from '../components/AdjacentNumbers';
import { getSsrPayload, clearSsrPayload, hasSsrMeta } from '../utils/ssrBootstrap';

const defaultCountry = countries.find((c) => c.iso2 === 'AU');

function PhoneNumberPage() {
  const { number: urlParam } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [phoneData, setPhoneData] = useState(null);
  const [redirectTo, setRedirectTo] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      setRedirectTo(null);

      try {
        const parsed = canonicalizeUrlParam(urlParam, defaultCountry);
        if (parsed.error) {
          setError(parsed.error);
          return;
        }

        if (parsed.redirect) {
          setRedirectTo(`/number/${parsed.redirect}`);
          return;
        }

        const canonicalId = parsed.canonicalId;

        const ssr = getSsrPayload();
        if (ssr?.route === 'number' && ssr.canonicalId === canonicalId && ssr.phoneData) {
          const needsAuthFetch = ssr.phoneData.isPrivate && user;
          if (!needsAuthFetch) {
            setPhoneData(ssr.phoneData);
            setLoading(false);
            if (!ssr.phoneData.isPrivate) clearSsrPayload();
            return;
          }
          clearSsrPayload();
        }

        let phone = await getPhoneNumber(canonicalId);
        const isNewListing = !phone;
        if (!phone) {
          phone = await upsertPhoneNumberStub(parsed);
        }

        const isOwner = user && phone.ownerId === user.uid;
        const isAdmin = user?.isAdmin;
        const canSeeReportDetails = !phone.isPrivate || isOwner || isAdmin;

        let reports = [];
        if (canSeeReportDetails) {
          reports = await getApprovedReports(canonicalId);
        }

        const countryCallingCode =
          phone.countryCallingCode ||
          parsed.countryCallingCode ||
          parsed.country?.callingCode ||
          '';
        const nationalNumber =
          phone.nationalNumber || parsed.nationalNumber || '';

        const adjacentIds = getAdjacentCanonicalIds(
          canonicalId,
          countryCallingCode,
          nationalNumber
        );
        const existingAdjacent = await getPhoneNumbersByIds(adjacentIds);
        const adjacentMap = new Map(
          existingAdjacent.map((p) => [p.canonicalId || p.id, p])
        );
        const adjacent = adjacentIds.map((id) => {
          const existing = adjacentMap.get(id);
          const cc =
            existing?.countryCallingCode || countryCallingCode || '';
          const national =
            existing?.nationalNumber ||
            (id.startsWith(cc) ? id.slice(cc.length) : '');
          const iso = existing?.iso2 || phone.iso2 || parsed.iso2 || '';
          if (existing) {
            return {
              canonicalId: id,
              displayE164: existing.displayE164 || `+${id}`,
              countryCallingCode: cc,
              nationalNumber: national,
              iso2: iso,
              totalReports: existing.totalReports ?? 0,
            };
          }
          return {
            canonicalId: id,
            displayE164: `+${id}`,
            countryCallingCode: cc,
            nationalNumber: national,
            iso2: iso,
            totalReports: 0,
          };
        });

        const isPrivatePublic = phone.isPrivate && !isOwner && !isAdmin;

        const data = {
          ...phone,
          canonicalId,
          isPrivate: phone.isPrivate,
          displayE164: phone.displayE164 || parsed.displayE164,
          country_name: phone.countryName || parsed.countryName,
          countryCallingCode,
          nationalNumber,
          iso2: phone.iso2 || parsed.iso2,
          total_reports: phone.totalReports ?? 0,
          trust_score: phone.trustScore ?? 0.5,
          spam_score: phone.spamScore ?? 0,
          scam_score: phone.scamScore ?? 0,
          nuisance_score: phone.nuisanceScore ?? phone.spamScore ?? 0,
          displayCompanyName: phone.displayCompanyName || '',
          last_reported_at: phone.lastReportedAt,
          reports: reports.map((r) => ({
            id: r.id,
            comment: r.comment,
            category_id: r.categoryId,
            category_name: r.categoryName || r.categoryId,
            alert_tier: r.alertTier || getCategoryById(r.categoryId).alertTier,
            company_name: r.companyName || '',
            created_at: r.createdAt?.toDate?.()?.toISOString() || r.createdAt,
            is_spam: r.isSpam,
          })),
          isEmpty: (phone.totalReports ?? 0) === 0 && reports.length === 0,
          canSeeReportDetails,
          isNewListing,
          adjacent,
        };

        setPhoneData(data);

        if (hasSsrMeta()) {
          return;
        }

        const displayLines = getPhoneDisplayLines(data);
        const display = displayLines.international;
        document.title = isPrivatePublic
          ? `${display} (Private listing) - WhoDatNumber`
          : `${display} - Phone Number Lookup | WhoDatNumber`;

        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.setAttribute('name', 'description');
          document.head.appendChild(metaDescription);
        }
        const reportCount = data.total_reports || 0;
        metaDescription.setAttribute(
          'content',
          isPrivatePublic
            ? `${display} is listed as a private individual. Spam and trust scores are shown; report comments are not public.`
            : data.isEmpty
              ? `No community reports yet for ${display}. Be the first to report this number.`
              : `${reportCount} community report(s) for ${display}. Check spam risk and caller information.`
        );

        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
          canonical = document.createElement('link');
          canonical.setAttribute('rel', 'canonical');
          document.head.appendChild(canonical);
        }
        const base = window.location.origin;
        canonical.setAttribute('href', `${base}/number/${canonicalId}`);

        if (phone.isPrivate && !isOwner && !isAdmin) {
          let robots = document.querySelector('meta[name="robots"]');
          if (!robots) {
            robots = document.createElement('meta');
            robots.setAttribute('name', 'robots');
            document.head.appendChild(robots);
          }
          robots.setAttribute('content', 'noindex');
        }
      } catch (err) {
        console.error(err);
        setError('Error loading phone number: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [urlParam, user]);

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">Phone Number Information</h1>
      {phoneData && (() => {
        const lines = getPhoneDisplayLines(phoneData);
        return (
          <div className="text-gray-600 mb-6">
            <p>{lines.international}</p>
            {lines.local && (
              <p className="text-sm mt-0.5">Local: {lines.local}</p>
            )}
            <p className="text-sm mt-1">
              {phoneData.isPrivate ? 'Private listing' : ''}
              {phoneData.isPrivate && phoneData.country_name ? ' · ' : ''}
              {phoneData.country_name || ''}
            </p>
          </div>
        );
      })()}
      {phoneData && (
        <>
          <PhoneNumberDetails
            phoneNumber={phoneData}
            onClaimed={async () => {
              const phone = await getPhoneNumber(phoneData.canonicalId);
              if (phone) {
                setPhoneData((prev) => ({
                  ...prev,
                  ownerId: phone.ownerId,
                  verified: phone.verified,
                }));
              }
            }}
            onReportSubmitted={async () => {
            const phone = await getPhoneNumber(phoneData.canonicalId);
            const reports = phoneData.canSeeReportDetails
              ? await getApprovedReports(phoneData.canonicalId)
              : [];
            const isOwner = user && phone?.ownerId === user.uid;
            const isAdmin = user?.isAdmin;
            setPhoneData((prev) => ({
              ...prev,
              ...phone,
              isPrivate: phone?.isPrivate ?? prev.isPrivate,
              canSeeReportDetails:
                !phone?.isPrivate || isOwner || isAdmin,
              total_reports: phone?.totalReports ?? prev.total_reports,
              trust_score: phone?.trustScore ?? prev.trust_score,
              spam_score: phone?.spamScore ?? prev.spam_score,
              scam_score: phone?.scamScore ?? 0,
              nuisance_score: phone?.nuisanceScore ?? phone?.spamScore ?? 0,
              displayCompanyName: phone?.displayCompanyName || '',
              isEmpty: (phone?.totalReports ?? 0) === 0 && reports.length === 0,
              reports: reports.map((r) => ({
                id: r.id,
                comment: r.comment,
                category_id: r.categoryId,
                category_name: r.categoryName || r.categoryId,
                alert_tier: r.alertTier || getCategoryById(r.categoryId).alertTier,
                company_name: r.companyName || '',
                created_at: r.createdAt?.toDate?.()?.toISOString() || r.createdAt,
              })),
            }));
          }}
          />
          <AdjacentNumbers
            currentId={phoneData.canonicalId}
            adjacent={phoneData.adjacent}
          />
        </>
      )}
    </div>
  );
}

export default PhoneNumberPage;

